import { Account, Group, Maintenance, PagedResult, Quotation } from '@interfaces';

type AuthenticationInfoStatus =
  'UNCONFIRMED' |
  'CONFIRMED' |
  'ARCHIVED' |
  'COMPROMISED' |
  'RESET_REQUIRED' |
  'FORCE_CHANGE_PASSWORD' |
  'UNKNOWN';

type UserRole = 'owner' | 'admin' | 'default';

type UserType = 'all' | 'admin' | 'requester';

export interface User {
  id?: string;
  name: string;
  email?: string;
  type?: UserType;
  role?: UserRole;
  avatarUrl?: string;
  archived?: boolean;
  createdAt?: string;
  emailHash?: string;
  fieldNewsEmailHash?: string;
  loginIntegrationEnabled?: boolean;
  loginProvider?: string;
  groups?: Group[];
  preferences?: UserPreferences;
  account?: Account;
  authenticationInfo?: AuthenticationInfo;
  groupsConnection?: PagedResult<Group>;
}

export interface UserPreferences {
  featureOpenedMaintenanceSendEmailNotification: Boolean;
}

export interface AuthenticationInfo {
  enabled?: boolean | 'UNKNOWN';
  status?: AuthenticationInfoStatus;
}

export interface MfaPreferences {
  enabledMfaTypes: string[];
  softwareTokenEnabled: boolean;
}

export interface RecoveryCode {
  plainTextCode: string;
  createdAt: string;
  expiredAt?: string;
  status: 'CURRENT_USED' | 'CURRENT_ACTIVE';
}

export function toLevel(role: string): number {
  const map = {
    default: 0,
    admin: 1,
    owner: 2
  };

  return map[role] || 0;
}

export enum NotificationTypeEnum {
  ticketCanceled = 'ticketCanceled',
  quotationCreated = 'quotationCreated',
  maintenanceCanceled = 'maintenanceCanceled',
  maintenanceCommented = 'maintenanceCommented',
  maintenanceOnNegotiation = 'maintenanceOnNegotiation',
  maintenanceSolutionDeadlineAtRisk = 'maintenanceSolutionDeadlineAtRisk',
  maintenanceResponseDeadlineAtRisk = 'maintenanceResponseDeadlineAtRisk',
  maintenanceSolutionDeadlineViolated = 'maintenanceSolutionDeadlineViolated',
  maintenanceResponseDeadlineViolated = 'maintenanceResponseDeadlineViolated',
  pendencyCreated = 'pendencyCreated',
  pendencyReplied = 'pendencyReplied',
  pendencyCompleted = 'pendencyCompleted',
  pendencyTransferred = 'pendencyTransferred'
}

export interface Notification {
  id: string;
  readAt: string;
  createdAt: string;
  type: NotificationTypeEnum;
  data: {
    author?: Partial<User>;
    comment?: Partial<Comment>;
    quotation?: Partial<Quotation>;
    pendency?: Partial<PendencyNotification>;
    maintenance?: Partial<MaintenanceNotification>;
    pendencyEvent?: Partial<PendencyEventNotification>
  };
}

export interface PendencyNotification {
  id: string;
  identifier: string;
  treatmentStatus: PendencyTreatmentStatusEnum;
}

export interface PendencyEventNotification {
  id: string;
  message: string;
}

export enum PendencyTreatmentStatusEnum {
  company = 'company',
  enterprise = 'enterprise'
}

export interface MaintenanceNotification extends Maintenance {
  firstVisitRiskAt: string;
  deadlineRiskAt: string;
}

export interface Comment {
  type: string;
  message: string;
  postedBy: Author;
  createdAt: string;
  maintenance: Maintenance;
}

export interface Author {
  id: string;
  name: string;
  type: string;
  avatarUrl: string;
}

export interface UserDialogData {
  user: User;
  currentUser?: User;
  isViewMode?: boolean;
}
